import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { adminFoundationApi, ingestionApi } from '../../services/api';
import { toast } from '../../stores';
import {
  NexusCard, NexusButton, NexusInput, NexusTextarea,
  NexusSelect, NexusModal, NexusModalFooter, NexusBadge,
} from '../../components/nexus-ui';
import {
  Plus, Database, Trash2, Upload, FileText, Search, Loader2, CheckCircle2,
  XCircle, ChevronDown, ChevronUp, Globe, MessageSquare, Brain,
  BarChart3, Tag, Send, Link2, Eye, Sparkles,
} from 'lucide-react';
import { KBDrawingsPanel } from '../../components/knowledge/KBDrawingsPanel';
import { adminDrawingsApi } from '../../services/adminDrawings';

const CATEGORIES = [
  { value: 'general', label: 'General' },
  { value: 'epc_standards', label: 'EPC/EPCM Standards' },
  { value: 'procurement', label: 'Procurement & Contracts' },
  { value: 'engineering', label: 'Engineering Documents' },
  { value: 'cost_schedule', label: 'Cost & Schedule' },
  { value: 'safety', label: 'Safety & Compliance' },
  { value: 'industry', label: 'Industry Knowledge' },
  { value: 'legal', label: 'Legal & Regulatory' },
  { value: 'medical', label: 'Medical & Healthcare' },
  { value: 'finance', label: 'Finance & Banking' },
  { value: 'technology', label: 'Technology & Software' },
  { value: 'operations', label: 'Operations & Logistics' },
  { value: 'hr', label: 'Human Resources' },
  { value: 'custom', label: 'Custom' },
];

const DOMAINS = [
  { value: 'general', label: 'General Knowledge' },
  { value: 'oil_gas', label: 'Oil & Gas' },
  { value: 'construction', label: 'Construction & EPC' },
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'fintech', label: 'FinTech & Banking' },
  { value: 'legal', label: 'Legal' },
  { value: 'education', label: 'Education' },
  { value: 'manufacturing', label: 'Manufacturing' },
  { value: 'retail', label: 'Retail & E-Commerce' },
  { value: 'saas', label: 'SaaS & Technology' },
];

const VISIBILITY = [
  { value: 'public', label: 'Public' },
  { value: 'internal', label: 'Internal' },
  { value: 'restricted', label: 'Restricted' },
];

const PRIORITY = [
  { value: 'primary', label: 'Primary Source' },
  { value: 'secondary', label: 'Secondary Reference' },
  { value: 'reference', label: 'Supplementary' },
];

const PIE_COLORS = ['#1E4E8C', '#F26B1F', '#2E70AD', '#92580A', '#7A1F1F', '#7A1F1F', '#5790C9', '#F26B1F'];

// Upload Zone — accepts ALL file formats, no size limit
function UploadZone({ onFilesSelected, isProcessing }) {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) onFilesSelected(files);
  }, [onFilesSelected]);

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setIsDragOver(true); }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={handleDrop}
      className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
        isDragOver ? 'border-nexus-accent-mint bg-nexus-accent-mint/5' : 'border-nexus-border-emphasized hover:border-white/20'
      } ${isProcessing ? 'opacity-50 pointer-events-none' : ''}`}
      onClick={() => {
        if (isProcessing) return;
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = true;
        input.onchange = (e) => {
          const files = Array.from(e.target.files);
          if (files.length > 0) onFilesSelected(files);
        };
        input.click();
      }}
      data-testid="upload-zone"
    >
      <Upload size={32} className={`mx-auto mb-3 ${isDragOver ? 'text-nexus-accent-mint' : 'text-nexus-text-tertiary'}`} />
      <p className="text-sm font-medium text-nexus-text-primary">Drop files here or click to upload</p>
      <p className="text-xs text-nexus-text-tertiary mt-1">All file formats supported — No size limit</p>
    </div>
  );
}

// =================== KB Insights Dashboard ===================
function KBInsights({ kbId }) {
  const { data: insights, isLoading } = useQuery({
    queryKey: ['kbInsights', kbId],
    queryFn: () => adminFoundationApi.getInsights(kbId).then(r => r.data),
    enabled: !!kbId,
  });

  if (isLoading) return <div className="text-center py-8 text-nexus-text-tertiary text-sm">Loading insights...</div>;
  if (!insights) return null;

  const catData = Object.entries(insights.category_distribution || {}).map(([name, value]) => ({ name, value }));

  return (
    <div className="space-y-4" data-testid="kb-insights">
      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Chunks', value: insights.total_chunks, icon: Database, color: '#1E4E8C' },
          { label: 'Tokens', value: (insights.total_tokens || 0).toLocaleString(), icon: Brain, color: '#F26B1F' },
          { label: 'Files', value: insights.total_files, icon: FileText, color: '#2E70AD' },
          { label: 'Categories', value: Object.keys(insights.category_distribution || {}).length, icon: Tag, color: '#92580A' },
        ].map(s => (
          <div key={s.label} className="p-3 rounded-lg bg-nexus-bg-tertiary border border-nexus-border-subtle">
            <s.icon size={16} style={{ color: s.color }} className="mb-1" />
            <p className="text-lg font-bold text-nexus-text-primary">{s.value}</p>
            <p className="text-xs text-nexus-text-tertiary">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Charts */}
      {catData.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <NexusCard variant="glass" hover={false}>
            <div className="p-4">
              <h4 className="text-sm font-medium text-nexus-text-primary mb-3">Category Distribution</h4>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={catData} cx="50%" cy="50%" innerRadius={40} outerRadius={70} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                    {catData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: 'var(--nexus-bg-secondary)', border: '1px solid var(--nexus-border-subtle)', borderRadius: '8px', fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </NexusCard>
          <NexusCard variant="glass" hover={false}>
            <div className="p-4">
              <h4 className="text-sm font-medium text-nexus-text-primary mb-3">Chunks by Category</h4>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={catData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--nexus-border-subtle)" />
                  <XAxis type="number" tick={{ fill: 'var(--nexus-text-tertiary)', fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fill: 'var(--nexus-text-secondary)', fontSize: 11 }} width={100} />
                  <Bar dataKey="value" fill="#1E4E8C" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </NexusCard>
        </div>
      )}

      {/* Source Files */}
      {insights.source_files?.length > 0 && (
        <div className="p-4 rounded-lg bg-nexus-bg-tertiary border border-nexus-border-subtle">
          <h4 className="text-sm font-medium text-nexus-text-primary mb-2">Source Files ({insights.source_files.length})</h4>
          <div className="flex flex-wrap gap-1.5">
            {insights.source_files.map((f, i) => (
              <span key={i} className="px-2 py-1 text-[11px] bg-nexus-accent-violet/10 text-nexus-accent-violet rounded-md">{f}</span>
            ))}
          </div>
        </div>
      )}

      {/* Recent Ingestions */}
      {insights.recent_ingestions?.length > 0 && (
        <div className="p-4 rounded-lg bg-nexus-bg-tertiary border border-nexus-border-subtle">
          <h4 className="text-sm font-medium text-nexus-text-primary mb-2">Recent Activity</h4>
          <div className="space-y-1.5">
            {insights.recent_ingestions.slice(0, 5).map((r, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-nexus-text-secondary truncate flex-1">{r.source}</span>
                <NexusBadge variant="info">{r.category}</NexusBadge>
                <span className="text-nexus-text-tertiary ml-2">{r.tokens} tokens</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// =================== URL Training ===================
function URLTraining({ kbId }) {
  const [url, setUrl] = useState('');
  const [category, setCategory] = useState('web-content');
  const queryClient = useQueryClient();

  const trainMutation = useMutation({
    mutationFn: (data) => adminFoundationApi.trainFromUrl(kbId, data),
    onSuccess: (res) => {
      toast.success(`Ingested ${res.data.chunks_created} chunks from URL`);
      setUrl('');
      queryClient.invalidateQueries({ queryKey: ['kbInsights', kbId] });
      queryClient.invalidateQueries({ queryKey: ['foundationKBs'] });
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to ingest URL'),
  });

  return (
    <div className="space-y-3" data-testid="url-training">
      <div className="flex gap-2">
        <div className="flex-1">
          <NexusInput
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://docs.example.com/knowledge-article"
            data-testid="url-input"
          />
        </div>
        <NexusSelect options={CATEGORIES} value={category} onChange={setCategory} />
        <NexusButton
          onClick={() => trainMutation.mutate({ url, category })}
          isLoading={trainMutation.isPending}
          disabled={!url.trim()}
          data-testid="ingest-url-btn"
        >
          <Globe size={14} /> Ingest
        </NexusButton>
      </div>
      <p className="text-xs text-nexus-text-tertiary">Paste a URL — content will be scraped, chunked, and embedded into the KB automatically.</p>
    </div>
  );
}

// =================== Chat Training ===================
function ChatTraining({ kbId }) {
  const [content, setContent] = useState('');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('chat-training');
  const [history, setHistory] = useState([]);
  const queryClient = useQueryClient();

  const trainMutation = useMutation({
    mutationFn: (data) => adminFoundationApi.trainFromChat(kbId, data),
    onSuccess: (res) => {
      const msg = { role: 'user', content };
      const reply = { role: 'system', content: `Added ${res.data.chunks_created} chunks (${res.data.total_tokens} tokens). Preview: ${res.data.processed_content?.slice(0, 200)}...` };
      setHistory(p => [...p, msg, reply]);
      setContent('');
      queryClient.invalidateQueries({ queryKey: ['kbInsights', kbId] });
      queryClient.invalidateQueries({ queryKey: ['foundationKBs'] });
      toast.success(`Knowledge added: ${res.data.chunks_created} chunks`);
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed to train'),
  });

  return (
    <div className="space-y-3" data-testid="chat-training">
      <div className="flex gap-2 mb-2">
        <NexusInput value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Topic title (e.g. Safety Protocols)" className="flex-1" />
        <NexusSelect options={CATEGORIES} value={category} onChange={setCategory} />
      </div>
      {/* Chat History */}
      {history.length > 0 && (
        <div className="max-h-48 overflow-y-auto space-y-2 p-3 rounded-lg bg-nexus-bg-primary border border-nexus-border-subtle">
          {history.map((m, i) => (
            <div key={i} className={`text-xs p-2 rounded-lg ${m.role === 'user' ? 'bg-nexus-accent-mint/10 text-nexus-text-primary ml-8' : 'bg-nexus-bg-tertiary text-nexus-text-secondary mr-8'}`}>
              <span className="font-medium">{m.role === 'user' ? 'You' : 'System'}:</span> {m.content}
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <NexusTextarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          placeholder="Type knowledge content, Q&A pairs, facts, procedures, or any information to train the KB with..."
          rows={3}
        />
      </div>
      <div className="flex justify-end">
        <NexusButton
          onClick={() => trainMutation.mutate({ content, title: title || 'Chat Training', category })}
          isLoading={trainMutation.isPending}
          disabled={!content.trim()}
          data-testid="train-chat-btn"
        >
          <Send size={14} /> Add to Knowledge Base
        </NexusButton>
      </div>
      <p className="text-xs text-nexus-text-tertiary">Cognara Engine · Ultra 4.7 will extract and structure your input before embedding it into the KB.</p>
    </div>
  );
}

// =================== MAIN COMPONENT ===================
const AdminKnowledge = () => {
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [selectedKB, setSelectedKB] = useState(null);
  const [activeTab, setActiveTab] = useState('insights');
  const [uploadCategory, setUploadCategory] = useState('general');
  const [uploadQueue, setUploadQueue] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [expandedChunks, setExpandedChunks] = useState({});
  const [tagInput, setTagInput] = useState('');
  const [form, setForm] = useState({
    name: '', description: '', category: 'general', domain: 'general',
    tags: [], language: 'en', source_priority: 'primary', visibility: 'internal',
  });

  const { data: kbs, isLoading } = useQuery({
    queryKey: ['foundationKBs'],
    queryFn: () => adminFoundationApi.listKBs().then(r => r.data),
  });

  const { data: chunks } = useQuery({
    queryKey: ['foundationChunks', selectedKB?.id],
    queryFn: () => adminFoundationApi.listChunks(selectedKB.id).then(r => r.data),
    enabled: !!selectedKB && activeTab === 'chunks',
  });

  const createMutation = useMutation({
    mutationFn: (data) => adminFoundationApi.createKB(data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['foundationKBs'] });
      toast.success('Knowledge Base created');
      setShowCreate(false);
      setSelectedKB(res.data);
      setForm({ name: '', description: '', category: 'general', domain: 'general', tags: [], language: 'en', source_priority: 'primary', visibility: 'internal' });
    },
    onError: (err) => toast.error(err.response?.data?.detail || 'Failed'),
  });

  const deleteChunkMutation = useMutation({
    mutationFn: (id) => adminFoundationApi.deleteChunk(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['foundationChunks'] });
      queryClient.invalidateQueries({ queryKey: ['foundationKBs'] });
      queryClient.invalidateQueries({ queryKey: ['kbInsights'] });
      toast.success('Chunk deleted');
    },
  });

  // File upload handler
  const handleFilesSelected = useCallback(async (files) => {
    if (!selectedKB) { toast.error('Select a KB first'); return; }
    setIsUploading(true);
    const startIdx = uploadQueue.length;
    const newQueue = files.map(f => ({ file: f, status: { stage: 'queued', progress: 0, detail: 'Queued' } }));
    setUploadQueue(prev => [...prev, ...newQueue]);
    for (let i = 0; i < files.length; i++) {
      const idx = startIdx + i;
      try {
        const fd = new FormData();
        fd.append('file', files[i]);
        fd.append('kb_id', selectedKB.id);
        fd.append('kb_type', 'foundation');
        fd.append('category', uploadCategory);
        const res = await ingestionApi.uploadDocumentAsync(fd);
        const taskId = res.data.task_id;
        await new Promise((resolve) => {
          const iv = setInterval(async () => {
            try {
              const p = await ingestionApi.getProgress(taskId);
              setUploadQueue(prev => prev.map((item, j) => j === idx ? { ...item, status: p.data } : item));
              if (p.data.stage === 'completed' || p.data.stage === 'error') { clearInterval(iv); resolve(); }
            } catch { clearInterval(iv); resolve(); }
          }, 800);
          setTimeout(() => { clearInterval(iv); resolve(); }, 300000);
        });
      } catch (err) {
        setUploadQueue(prev => prev.map((item, j) => j === idx ? { ...item, status: { stage: 'error', progress: 0, detail: err.message } } : item));
      }
    }
    queryClient.invalidateQueries({ queryKey: ['foundationKBs'] });
    queryClient.invalidateQueries({ queryKey: ['foundationChunks'] });
    queryClient.invalidateQueries({ queryKey: ['kbInsights'] });
    setIsUploading(false);
  }, [selectedKB, uploadCategory, uploadQueue.length, queryClient]);

  const addTag = () => {
    const t = tagInput.trim();
    if (t && !form.tags.includes(t)) {
      setForm(p => ({ ...p, tags: [...p.tags, t] }));
      setTagInput('');
    }
  };

  const TABS = [
    { id: 'insights', label: 'Insights', icon: BarChart3 },
    { id: 'upload', label: 'Upload Files', icon: Upload },
    { id: 'url', label: 'URL Training', icon: Link2 },
    { id: 'chat', label: 'Chat Training', icon: MessageSquare },
    { id: 'chunks', label: 'Chunks', icon: Database },
    { id: 'drawings', label: 'Drawings', icon: Sparkles },
  ];

  return (
    <div className="space-y-6" data-testid="admin-knowledge">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-nexus-text-primary">Knowledge Base</h2>
          <p className="text-nexus-text-secondary text-sm mt-1">Foundation knowledge powering the AI — Cognara Engine · Ultra 4.7</p>
        </div>
        <NexusButton onClick={() => setShowCreate(true)} data-testid="create-kb-btn">
          <Plus size={16} /> Create Knowledge Base
        </NexusButton>
      </div>

      {/* KB List */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {isLoading ? (
          <div className="col-span-3 text-center py-8 text-nexus-text-tertiary">Loading...</div>
        ) : (kbs || []).length === 0 ? (
          <div className="col-span-3 text-center py-12">
            <Database size={40} className="mx-auto mb-3 text-nexus-text-tertiary" />
            <p className="text-nexus-text-secondary">No knowledge bases yet</p>
            <p className="text-xs text-nexus-text-tertiary mt-1">Create one to start training your AI</p>
          </div>
        ) : (kbs || []).map(kb => (
          <motion.div key={kb.id} whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}>
            <NexusCard
              variant={selectedKB?.id === kb.id ? 'default' : 'glass'}
              className={`cursor-pointer ${selectedKB?.id === kb.id ? 'ring-1 ring-nexus-accent-mint' : ''}`}
              onClick={() => { setSelectedKB(kb); setActiveTab('insights'); }}
            >
              <div className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <Database size={20} className="text-nexus-accent-mint" />
                  <div className="flex gap-1">
                    <NexusBadge variant="info">{kb.category || 'general'}</NexusBadge>
                    <NexusBadge variant={kb.visibility === 'public' ? 'success' : 'premium'}>{kb.visibility || 'internal'}</NexusBadge>
                  </div>
                </div>
                <h3 className="font-heading font-semibold text-nexus-text-primary text-sm">{kb.name}</h3>
                <p className="text-xs text-nexus-text-tertiary mt-1 line-clamp-2">{kb.description}</p>
                <div className="flex items-center gap-4 mt-3 text-xs text-nexus-text-tertiary">
                  <span>{kb.total_chunks || 0} chunks</span>
                  <span>{((kb.total_tokens || 0) / 1000).toFixed(1)}K tokens</span>
                  <span>{kb.total_files || 0} files</span>
                </div>
                {(kb.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {kb.tags.slice(0, 3).map(t => (
                      <span key={t} className="px-1.5 py-0.5 text-[10px] bg-nexus-accent-violet/10 text-nexus-accent-violet rounded">{t}</span>
                    ))}
                    {kb.tags.length > 3 && <span className="text-[10px] text-nexus-text-tertiary">+{kb.tags.length - 3}</span>}
                  </div>
                )}
              </div>
            </NexusCard>
          </motion.div>
        ))}
      </div>

      {/* Selected KB Detail */}
      {selectedKB && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <NexusCard variant="glass" hover={false}>
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-heading text-lg font-semibold text-nexus-text-primary">{selectedKB.name}</h3>
                  <p className="text-xs text-nexus-text-tertiary mt-0.5">{selectedKB.description}</p>
                </div>
                <div className="flex gap-2">
                  {(selectedKB.tags || []).map(t => <NexusBadge key={t} variant="info">{t}</NexusBadge>)}
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-1 border-b border-nexus-border-subtle mb-4 overflow-x-auto">
                {TABS.map(tab => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                        activeTab === tab.id
                          ? 'border-nexus-accent-mint text-nexus-accent-mint'
                          : 'border-transparent text-nexus-text-tertiary hover:text-nexus-text-secondary'
                      }`}
                      data-testid={`kb-tab-${tab.id}`}
                    >
                      <Icon size={14} /> {tab.label}
                    </button>
                  );
                })}
              </div>

              {/* Tab Content */}
              {activeTab === 'insights' && <KBInsights kbId={selectedKB.id} />}
              {activeTab === 'drawings' && (
                <KBDrawingsPanel
                  kbId={selectedKB.id}
                  api={adminDrawingsApi}
                  queryKey="admin-foundation-drawings"
                />
              )}

              {activeTab === 'upload' && (
                <div className="space-y-4">
                  <div className="flex gap-3 items-end">
                    <div className="flex-1">
                      <NexusSelect label="Category" options={CATEGORIES} value={uploadCategory} onChange={setUploadCategory} />
                    </div>
                  </div>
                  <UploadZone onFilesSelected={handleFilesSelected} isProcessing={isUploading} />
                  {uploadQueue.length > 0 && (
                    <div className="space-y-2">
                      {uploadQueue.map((item, i) => {
                        const s = item.status;
                        return (
                          <div key={i} className="p-3 rounded-lg bg-nexus-bg-tertiary border border-nexus-border-subtle">
                            <div className="flex items-center gap-3 mb-1.5">
                              <FileText size={14} className="text-nexus-accent-violet flex-shrink-0" />
                              <span className="text-xs text-nexus-text-primary truncate flex-1">{item.file.name}</span>
                              {s.stage === 'completed' && <CheckCircle2 size={14} className="text-green-400" />}
                              {s.stage === 'error' && <XCircle size={14} className="text-red-400" />}
                              {!['completed', 'error'].includes(s.stage) && <Loader2 size={14} className="animate-spin text-nexus-accent-mint" />}
                            </div>
                            <div className="w-full h-1 bg-nexus-bg-primary rounded-full overflow-hidden">
                              <div className={`h-full rounded-full transition-all ${s.stage === 'error' ? 'bg-red-500' : s.stage === 'completed' ? 'bg-green-500' : 'bg-nexus-accent-mint'}`} style={{ width: `${s.progress || 0}%` }} />
                            </div>
                            <p className="text-[11px] text-nexus-text-tertiary mt-1">{s.detail || s.stage}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'url' && <URLTraining kbId={selectedKB.id} />}
              {activeTab === 'chat' && <ChatTraining kbId={selectedKB.id} />}

              {activeTab === 'chunks' && (
                <div className="space-y-2">
                  {!chunks || chunks.length === 0 ? (
                    <p className="text-center py-6 text-nexus-text-tertiary text-sm">No chunks yet. Upload documents or train via Chat/URL.</p>
                  ) : chunks.map((c, i) => (
                    <div key={c.id || i} className="p-3 rounded-lg bg-nexus-bg-tertiary border border-nexus-border-subtle">
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <NexusBadge variant="info">{c.category}</NexusBadge>
                          <span className="text-xs text-nexus-text-tertiary">{c.source_file}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <span className="text-[11px] text-nexus-text-tertiary">{c.token_count} tokens</span>
                          <button onClick={() => setExpandedChunks(p => ({ ...p, [c.id]: !p[c.id] }))} className="p-1 text-nexus-text-tertiary hover:text-nexus-text-secondary">
                            {expandedChunks[c.id] ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                          </button>
                          <button onClick={() => deleteChunkMutation.mutate(c.id)} className="p-1 text-nexus-text-tertiary hover:text-red-400">
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                      <p className={`text-xs text-nexus-text-secondary ${expandedChunks[c.id] ? '' : 'line-clamp-2'}`}>{c.content}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </NexusCard>
        </motion.div>
      )}

      {/* Create KB Modal */}
      <NexusModal isOpen={showCreate} onClose={() => setShowCreate(false)} title="Create Knowledge Base" description="Set up a new foundation knowledge base with detailed classification">
        <div className="space-y-4">
          <NexusInput label="Name" value={form.name} onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. EPC Best Practices" data-testid="kb-name-input" />
          <NexusTextarea label="Description" value={form.description} onChange={(e) => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Describe what knowledge this base will contain..." rows={2} />
          <div className="grid grid-cols-2 gap-3">
            <NexusSelect label="Category" options={CATEGORIES} value={form.category} onChange={(v) => setForm(p => ({ ...p, category: v }))} />
            <NexusSelect label="Domain" options={DOMAINS} value={form.domain} onChange={(v) => setForm(p => ({ ...p, domain: v }))} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <NexusSelect label="Source Priority" options={PRIORITY} value={form.source_priority} onChange={(v) => setForm(p => ({ ...p, source_priority: v }))} />
            <NexusSelect label="Visibility" options={VISIBILITY} value={form.visibility} onChange={(v) => setForm(p => ({ ...p, visibility: v }))} />
          </div>
          <NexusInput label="Language" value={form.language} onChange={(e) => setForm(p => ({ ...p, language: e.target.value }))} placeholder="en" />
          <div>
            <label className="text-xs font-medium text-nexus-text-secondary block mb-1.5">Tags</label>
            <div className="flex gap-2">
              <NexusInput value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="Add tag..." onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())} />
              <NexusButton variant="secondary" onClick={addTag}><Plus size={14} /></NexusButton>
            </div>
            {form.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {form.tags.map(t => (
                  <span key={t} className="inline-flex items-center gap-1 px-2 py-0.5 bg-nexus-accent-violet/10 text-nexus-accent-violet text-xs rounded-md">
                    {t}
                    <button onClick={() => setForm(p => ({ ...p, tags: p.tags.filter(x => x !== t) }))} className="hover:text-red-400">
                      <XCircle size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
        <NexusModalFooter>
          <NexusButton variant="ghost" onClick={() => setShowCreate(false)}>Cancel</NexusButton>
          <NexusButton onClick={() => createMutation.mutate(form)} isLoading={createMutation.isPending} data-testid="create-kb-submit">Create</NexusButton>
        </NexusModalFooter>
      </NexusModal>
    </div>
  );
};

export default AdminKnowledge;
